import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import IconArrow from 'src/assets/images/icons/arrow-left_ico.svg';
import IconCloud from 'src/assets/images/icons/cloud-sunny-color.svg';
import { CircularProgress } from '@mui/material';
import TransportMediumIcon from 'src/presentations/components/molecules/card/TransportMediumIcon';
import { beautifyEnum } from 'src/utils/utility';
import { MoreActionsMenu } from '../../organisms/dashboard/MoreActionsMenu';
import { MoreActionsButton } from '../../organisms/dashboard/MoreActionsButton';
import { TransportMedium } from '../../molecules/card/types';
import { DashboardFilterHeader } from '../../organisms/dashboard/DashboardFilterHeader';
import { useDashboard } from './DashboardContext';
import { AuditDashboardTour } from 'src/presentations/guided-tours/tours/AuditDashboardTour';

export const DashboardHeader = ({ tour }: { tour: AuditDashboardTour }) => {
  const [searchParams] = useSearchParams();
  const modality = searchParams.get('modality');
  const providerCode = searchParams.get('provider');
  const [isLoading, setIsLoading] = useState(true);
  const [moreActionEl, setMoreActionEl] = useState<HTMLButtonElement | null>(null);

  const { dataStats } = useDashboard();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const onClickMoreAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMoreActionEl(event.currentTarget);
  };

  const onCloseMoreAction = () => {
    setMoreActionEl(null);
  };

  return (
    <div className='sticky top-0 flex items-center gap-20 px-20 xl:px-24 pt-18 xl:pt-24 pb-18 xl:pb-20 bg-white border-b border-neutral-20 mb-20 z-10'>
      <div className='flex-1 flex items-center gap-18'>
        <Link
          id={tour.registerStep('backButton')}
          to='/'
          className='w-40 h-40 rounded-full bg-neutral-10 border border-neutral-20 transition-all duration-300 hover:border-neutral-40 flex items-center justify-center'
        >
          <img src={IconArrow} alt='Back' />
        </Link>
        <div id={tour.registerStep('title')} className='flex flex-col'>
          <div className='flex items-center gap-10'>
            <div className='flex items-center justify-center'>
              <TransportMediumIcon medium={modality as TransportMedium} iconColor='#9F9F9F' />
            </div>
            <h2 className='text-16 xl:text-18 font-bold text-blue_grey-80 max-lg:text-18'>{beautifyEnum(providerCode)?.toUpperCase()}</h2>
            <MoreActionsButton id={tour.registerStep('moreActions')} onClickMoreAction={onClickMoreAction} />
          </div>
        </div>
        <MoreActionsMenu moreActionEl={moreActionEl} onCloseMoreAction={onCloseMoreAction} modality={modality} provider={providerCode} />
      </div>

      <div className='ml-auto flex items-center gap-16'>
        <div
          id={tour.registerStep('co2Widget')}
          className='py-6 pl-6 pr-32 border border-neutral-20 rounded-full flex items-center gap-8 xl:gap-12 min-w-[7rem] xl:min-w-[10rem]'
        >
          <div className='w-32 h-32 rounded-full bg-[#E3F5F1] flex items-center justify-center'>
            <img src={IconCloud} alt='' />
          </div>
          <div className='flex flex-col'>
            {isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <p className='text-14 leading-14 xl:text-16 xl:leading-16 font-bold'>{dataStats?.keymetrics?.totalCo2Emissions || 0} t</p>
            )}
            <p className='text-10 xl:text-12 text-neutral-60'>
              CO<sub>2</sub>e
            </p>
          </div>
        </div>
        <div className='w-1 h-16 bg-neutral-20' />
        <DashboardFilterHeader tour={tour} />
      </div>
    </div>
  );
};
